<template>
    <div>
        <div class="row tw-mt-2">
            <div class="col-md-6">
                <l-multi-filter
                    ref="multiFilter"
                    saved-view-type="survey"
                    :filter-types="filterTypes"
                    :show-custom-attributes="true"
                    :label="$t('surveys.details.filterBy')"
                    @update="onFilterChange"
                />
            </div>
            <div class="col-md-3">
                <l-select
                    v-if="showGroupBy2"
                    v-model="groupBy2"
                    style="margin-top: 0px;"
                    :options="[
                        ...availableSegmentationOptions.map(f => ({
                            value: f.value || f,
                            text: f.text || $t(`surveys.details.groupBy_${f}`),
                        })),
                    ]"
                    :label="$t('surveys.details.segmentBy')"
                    @change="onGroupBy2Change"
                />

                <l-select
                    v-if="showSortBy"
                    v-model="sortBy"
                    style="margin-top: 0px;"
                    :options="[
                        {
                            value: '-avg_amt',
                            text: $t('surveys.details.labelSortListOrderAmount'),
                        },
                        {
                            value: '-question.impact',
                            text: $t('surveys.details.labelSortListOrderImpact'),
                        },
                    ]"
                    @change="onSortByChange"
                />
            </div>
            <div class="col-md-3">
                <l-select
                    v-show="showGroupBy1"
                    v-model="groupBy1"
                    style="margin-top: 0px;"
                    :options="allowedGroupBy1s"
                    @change="onGroupBy1Change"
                />
            </div>
        </div>
    </div>
</template>

<script>
import { mapGetters } from "vuex";

import LSelect from "@/components/LSelect";
import LMultiFilter from "./LMultiFilter.vue";

export default {
    components: {
        LSelect,
        LMultiFilter,
    },
    props: {
        survey: {
            type: Object,
            required: true,
        },
        allowedFilters: {
            type: Array,
            required: true,
        },
        showGroupBy2: {
            type: Boolean,
            required: false,
            default: false,
        },
        allowedGroupBy2s: {
            type: Array,
            required: false,
            default: () => [],
        },
        allowedGroupBy1s: {
            type: Array,
            required: false,
            default: () => [],
        },
        showSortBy: {
            type: Boolean,
            required: false,
            default: false,
        },
        showGroupBy1: {
            type: Boolean,
            required: false,
            default: false,
        },
        classifyOptions: {
            type: Array,
            required: false,
            default: () => [],
        },
    },
    data() {
        return {
            error: false,

            // 4 models to hold filter

            sortBy: "", // 3rd drop down
            groupBy1: "", // 4th drop down
            groupBy2: "",
            isMarkedAsSeen: true,

            filters: {}, // this is where we store our selected filters.
        };
    },
    computed: {
        ...mapGetters({
            rights: "users/current/rights",
            teamSettings: "users/current/teamSettings",
            user: "users/current/user",
            getCustomRoles: "teams/customrole/list",
            localTeams: "teams/local/listSortedByName",
            customAttributes: "users/customAttribute/list",
        }),
        accountName() {
            // company name
            return this.teamSettings.name;
        },
        demographicFilters() {
            return [
                "user",
                "manager",
                "localTeam",
                "location",
                "customRole",
                "gender",
                "age",
                "performance",
            ];
        },
        availableSegmentationOptions() {
            const survey = this.survey;
            const options = [...survey.sharedWithMe.filters] || this.demographicFilters || [];

            // ... add tenure (not available as filter)

            if (
                survey.sharedWithMe.level == "ALL" ||
                survey.sharedWithMe.scope.includes("TEAM_MANAGER")
            ) {
                options.push("tenure");
            }

            // ... add custom attributes

            this.customAttributes.forEach(a => {
                options.push({
                    value: a._id,
                    text: a.name,
                });
            });

            return options.filter(option => option !== "user");
        },

        filterTypes() {
            const survey = this.survey;

            let period = {};
            if (["ONCE_AFTER_START", "ONCE_BEFORE_END"].includes(survey.timing.frequency)) {
                const start = survey.periods[0].start;
                const options = [];

                let option = this.$moment(start);
                while (option.isBefore(this.$moment())) {
                    options.push({
                        name: option.format("YYYY-MM"),
                        _id: option.format("YYYY-MM"),
                    });
                    option = option.add(1, "month");
                }

                period = {
                    value: "period",
                    key: "period",
                    text: this.$t(`surveys.details.filterByPeriod`),
                    options,
                };
            } else {
                period = {
                    value: "period",
                    key: "period",
                    text: this.$t(`surveys.details.filterByPeriod`),
                    options: (survey.periods || []).map(p => ({
                        name: new Date(p.start).toDateString(),
                        // FIXME this is legacy logic, display start, but send end value
                        _id: p.end,
                    })),
                };
            }

            let filterTypes = [
                period,
                {
                    value: "localTeam",
                    key: "localTeam",
                    text: this.$t(`surveys.details.filterByLocalTeam`),
                    options:
                        !this.rights.S_CREATE && this.survey.sharedWithMe.level !== "ALL"
                            ? // for non-admins with limited access, return only teams that user is part of
                              this.localTeams.filter(
                                  t => this.user.localTeamMember?.includes(t._id) && !t.settings,
                              )
                            : // otherwise assume default (all teams)
                              [],
                },
                {
                    value: "manager",
                    key: "manager",
                    text: this.$t(`surveys.details.filterByManager`),
                },
                {
                    value: "user",
                    key: "user",
                    text: this.$t(`surveys.details.filterByUser`),
                },
                {
                    value: "location",
                    key: "location",
                    text: this.$t(`surveys.details.filterByLocation`),
                },
                {
                    value: "customRole",
                    key: "customRole",
                    text: this.$t(`surveys.details.filterByCustomRole`),
                    options: this.getCustomRoles.map(o => ({
                        name: o.name,
                        _id: o._id,
                    })),
                },
                {
                    value: "gender",
                    key: "gender",
                    text: this.$t(`surveys.details.filterByGender`),
                    options: ["male", "female", "diverse"].map(o => ({
                        _id: o,
                        name: this.$t(`surveys.details.filters.gender.${o}`),
                    })),
                },
                {
                    value: "age",
                    key: "age",
                    text: this.$t(`surveys.details.filterByAge`),
                    options: [
                        "under20inclusive",
                        "21-25",
                        "26-30",
                        "31-35",
                        "36-40",
                        "41-45",
                        "46-50",
                        "over50",
                    ].map(o => ({ _id: o, name: this.$t(`surveys.details.filters.age.${o}`) })),
                },
                {
                    value: "category",
                    key: "category",
                    text: this.$t(`surveys.details.filterByCategory`),
                    options:
                        survey.questions
                            ?.filter(q => q?.skill?.category)
                            .map(q => ({ name: q.skill.categoryName, _id: q.skill?.category })) // map to cateogry obj
                            .filter(
                                (category, idx, arr) =>
                                    arr.findIndex(
                                        existingCategory => existingCategory._id === category._id,
                                    ) === idx,
                            ) || [], // remove duplicate
                },
                {
                    value: "question",
                    key: "question",
                    text: this.$t(`surveys.details.filterByQuestion`),
                    options: (survey.questions || []).map(q => ({
                        name: q.skill?.name?.replace("%ACCOUNTNAME%", this.accountName),
                        _id: q.skill?._id,
                    })),
                },
                {
                    value: "sentiment",
                    key: "sentiment",
                    text: this.$t(`surveys.details.filterBySentiment`),
                    options: ["positive", "negative", "neutral"].map(s => ({
                        _id: s,
                        name: this.$t(`surveys.details.filters.sentiment.${s}`),
                    })),
                },
                {
                    value: "replied",
                    key: "replied",
                    text: this.$t(`surveys.details.filterByReplied`),
                    options: [
                        {
                            _id: "true",
                            name: this.$t("surveys.details.labelWithConversation"),
                        },
                        {
                            _id: "false",
                            name: this.$t("surveys.details.labelWithoutConversation"),
                        },
                    ],
                },
                {
                    value: "onlyShowUnseen",
                    key: "onlyShowUnseen",
                    text: this.$t(`surveys.details.filterByOnlyShowUnseen`),
                    options: [true].map(value => ({
                        _id: value,
                        name: this.$t(`surveys.details.filters.onlyShowUnseen.${value}`),
                    })),
                },
                {
                    value: "onlyShowAssigned",
                    key: "onlyShowAssigned",
                    text: this.$t(`surveys.details.filterByOnlyShowAssigned`),
                    options: [true].map(value => ({
                        _id: value,
                        name: this.$t(`surveys.details.filters.onlyShowAssigned.${value}`),
                    })),
                },
                {
                    value: "classify",
                    key: "classify",
                    text: this.$t(`surveys.details.filterByClassify`),
                    options: this.classifyOptions,
                },
            ];

            // define accessible filters: non-demographic allowedFilters + demographicFilters (either specified or generic)

            let accessibleFilters = this.allowedFilters
                .concat(this.survey.sharedWithMe.filters || this.demographicFilters || [])
                .filter(f => f !== "performance");

            return filterTypes.filter(f => accessibleFilters.includes(f.value));
        },
    },

    created() {
        this.initDefault();
        const { filters, groupBy1, groupBy2, sortBy } = this;
        this.$emit("change", "init", { groupBy1, groupBy2, filters, sortBy });
    },
    methods: {
        // parse Query params and or set default value.
        initDefault() {
            const { groupBy1, groupBy2, sortBy } = this.$route.query;

            this.groupBy1 = groupBy1 === undefined ? "question" : groupBy1;

            // groupBy2 check it permission first.
            if (groupBy2 && this.availableSegmentationOptions.includes(groupBy2)) {
                this.groupBy2 = groupBy2;
            } else {
                // FIXME if user doesn't have any permissions. we might need to show error.
                this.groupBy2 = this.availableSegmentationOptions[0] || "localTeam"; // FIXME default option
            }
            this.sortBy = sortBy || "-avg_amt";
            for (const key in this.allOptions) {
                this.filters[key] = this.allOptions[key].filter(o => o.selected);
            }

            this.$forceUpdate();
        },

        onFilterChange(filters) {
            this.filters = filters;
            this.$emit("change", "filters", {
                groupBy1: this.groupBy1,
                groupBy2: this.groupBy2,
                filters,
                sortBy: this.sortBy,
            });
        },

        onGroupBy2Change(v) {
            const { filters, groupBy1, sortBy } = this;
            const groupBy2 = v.target.value;
            this.groupBy2 = groupBy2;
            this.updateQueryParam();
            this.$emit("change", "groupBy2", {
                groupBy2,
                groupBy1,
                filters,
                sortBy,
            });
        },
        updateQueryParam() {
            const { groupBy1, groupBy2, sortBy } = this;
            const q = this.$route.query;
            if (groupBy1 !== q.groupBy1 || groupBy2 !== q.groupBy2 || sortBy !== q.sortBy) {
                const query = { ...this.$route.query, groupBy1, groupBy2, sortBy };
                this.$router.push({ query });
            }
        },

        onGroupBy1Change(v) {
            const { filters, groupBy2, sortBy } = this;
            const groupBy1 = v.target.value;
            this.groupBy1 = groupBy1;
            this.updateQueryParam();
            this.$emit("change", "groupBy1", {
                groupBy1,
                groupBy2,
                filters,
                sortBy,
            });
        },
        onSortByChange(v) {
            const { filters, groupBy2, groupBy1 } = this;
            const sortBy = v.target.value;
            this.sortBy = sortBy;
            this.updateQueryParam();
            this.$emit("change", "sortBy", { groupBy1, groupBy2, filters, sortBy });
        },
        selectOption(type, option) {
            const previousType = this.$refs.multiFilter.selectedFilterType;
            this.$refs.multiFilter.selectedFilterType = type;
            this.$refs.multiFilter.addFilter(option);
            this.$refs.multiFilter.selectedFilterType = previousType;
        },
    },
};
</script>
